import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import { TopikUserRoleType, TopikRole, TopikResponse, TopikListResponse } from './types';

/**
 * List users by role
 * @param params Object with role and pagination options
 * @returns Promise with array of users with the specified role
 */
export const listUsersByRole = async (params: {
    role: TopikRole;
    limit?: number;
    offset?: number;
}): Promise<{ data: TopikUserRoleType[]; total: number } | null> => {
    try {
        const res = await api.post<TopikListResponse<TopikUserRoleType>>('/topik/user', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });

        return res.data?.code === 200
            ? { data: res.data.data ?? [], total: res.data.total ?? 0 }
            : null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.code === 404) {
            return { data: [], total: 0 };
        }
        console.error('Failed to list users by role:', e);
        return null;
    }
};

/**
 * Get user role information
 * @param params Object with email or addressid
 * @returns Promise with user role data or null if failed
 */
export const getUserRole = async (params: {
    email?: string;
    addressid?: string;
}): Promise<TopikUserRoleType | null> => {
    try {
        const res = await api.post<TopikResponse<TopikUserRoleType>>('/topik/user', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });

        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.code === 404) {
            return null;
        }
        console.error('Failed to get user role:', e);
        return null;
    }
};

/**
 * Assign role to a user
 * @param params Object with email or addressid and role to assign
 * @returns Promise with assigned user role data or null if failed
 */
export const assignUserRole = async (params: {
    email?: string;
    addressid?: string;
    role: TopikRole;
}): Promise<TopikUserRoleType | null> => {
    try {
        const res = await api.put<TopikResponse<TopikUserRoleType>>('/topik/user', params, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });

        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to assign user role:', e);
        return null;
    }
};

/**
 * Remove user role
 * @param params Object with email or addressid
 * @returns Promise with success status
 */
export const removeUserRole = async (params: {
    email?: string;
    addressid?: string;
}): Promise<boolean> => {
    try {
        const res = await api.delete<TopikResponse<{ message: string }>>('/topik/user', {
            params,
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });

        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to remove user role:', e);
        return false;
    }
};
